import { CoachMark } from '@pmi/dsm-react';
import { CoachMarkStep } from '@pmi/dsm-react/dist/components/coachmark/types';
import { useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  agreementDetailsCoachMarkSteps,
  dashboardCoachMarkSteps,
  peopleAdminTabCoachMarkSteps,
  peopleUserTabCoachMarkSteps,
  productGroupCoachMarkSteps,
  reportDetailsCoachMarkSteps,
  reportOverviewCoachMarkSteps,
  updateStepSelector,
} from './coachMarkSteps';
import useBreakpoint, { SIZE_XS } from '../../hooks/useBreakpoint';
import { reportCoachMarks } from '../../analytics';
import toUrlFriendlyString from '../../utils/toUrlFriendlyString ';
import { accountSelector } from '../../store/shared/accountSlice';

export interface TutorialCoachMarksProps {
  btnTracking: (linkTitle: string, linkModule: string) => void;
}

const getTour = (pathname: string, currentAccountId: string) => {
  let steps: CoachMarkStep[] = [];
  let linkTitle: string = '';
  if (pathname.includes('/people')) {
    const selectedTab = document.querySelector(
      '.people .people-page-tabs [role="tablist"] .rts___tab___selected',
    );
    if (selectedTab) {
      const index = selectedTab.getAttribute('data-index');
      // user tab
      if (index === '0') {
        linkTitle = 'people:user';
        steps = peopleUserTabCoachMarkSteps;
      }
      // admin tab
      if (index === '1') {
        linkTitle = 'people:admin';
        steps = peopleAdminTabCoachMarkSteps;
      }
    }
  } else if (pathname.includes('/reports')) {
    // report details page
    if (pathname.includes('/sku')) {
      linkTitle = 'reports:details';
      steps = reportDetailsCoachMarkSteps;
      steps = updateStepSelector(
        steps,
        '.report_details-registered_users-table table tbody tr:first-child',
        '.report_details-registered_users-table p',
      );
    } else {
      // report overview page
      linkTitle = 'reports:overview';
      steps = reportOverviewCoachMarkSteps;
      if (!document.querySelector('.deal-tabs')) {
        steps = steps.filter((s) => s.selector !== '.deal-tabs');
      }
    }
  } else if (pathname.includes('/groups')) {
    linkTitle = 'product-groups';
    steps = productGroupCoachMarkSteps;
    if (!document.querySelector('.product_group-add-people_domain-btn')) {
      steps = steps.filter(
        (s) => s.selector !== '.product_group-add-people_domain-btn',
      );
    }

    if (
      !document.querySelector('.product_group-create-new-product_group-btn')
    ) {
      steps = steps.filter(
        (s) => s.selector !== '.product_group-create-new-product_group-btn',
      );
    }
  } else if (pathname.includes(`/${currentAccountId}/${currentAccountId}-`)) {
    linkTitle = 'agreement-details';
    steps = agreementDetailsCoachMarkSteps;
    if (!document.querySelector('.dashboard-add-people')) {
      steps = steps.filter((s) => s.selector !== '.dashboard-add-people');
    }
  } else {
    linkTitle = 'home';
    steps = dashboardCoachMarkSteps;
    if (
      document.querySelector(
        '.dashboard-alc table tbody tr:first-child td[colspan="8"]',
      )
    ) {
      steps = steps.map((s) => {
        const clone = { ...s };
        if (clone.selector === '.dashboard-alc table tbody tr:first-child') {
          clone.selector = '.agreement-list-card-cont';
          clone.description =
            'Your agreement information will be listed here when available.';
        }
        return clone;
      });
      steps = steps.filter(
        (s) =>
          s.selector !==
          '.dashboard-alc table tbody tr:first-child td:last-child button',
      );
    }
    // dashboard-alc
  }
  return { steps, linkTitle };
};

export default function TutorialCoachMarks({
  btnTracking,
}: TutorialCoachMarksProps) {
  const { currentAccountId } = useSelector(accountSelector);
  const bp = useBreakpoint();
  const location = useLocation();
  const [tour, setTour] = useState<{
    steps: CoachMarkStep[];
    linkTitle: string;
  }>({
    steps: [],
    linkTitle: '',
  });
  const [step, setStep] = useState(0);
  const [cta, setCta] = useState('start');
  const [linkModule, setLinkModule] = useState('');

  // avoid multiple rerender
  useEffect(() => {
    const { steps, linkTitle } = getTour(location.pathname, currentAccountId);
    setTour({ steps, linkTitle });
  }, [location.pathname]);

  useEffect(() => {
    if (tour.steps.length > 0) {
      const linkMod = `cm${tour.linkTitle}:${step + 1}:${
        tour.steps.length
      }:${toUrlFriendlyString(tour.steps[step].title)}`;
      setLinkModule(linkMod);
      reportCoachMarks(cta, linkMod);
    }
  }, [tour, step]);

  return (
    bp !== SIZE_XS &&
    tour.steps.length > 0 &&
    linkModule && (
      <CoachMark
        analyticsRegion={{ adoberegion: linkModule }}
        onChange={(currentIndex, nextIndex) => {
          setStep(nextIndex);
          if (currentIndex > nextIndex) {
            setCta('previous');
          } else {
            setCta('next');
          }
        }}
        onFinished={() => {
          btnTracking('end', linkModule);
        }}
        onSkipped={() => {
          btnTracking('skip', linkModule);
        }}
        onClose={() => {
          btnTracking('close', linkModule);
        }}
        steps={tour.steps}
        theme="dark"
        stepIndex={step}
      />
    )
  );
}
